import type {
  HarnessItemResult,
  HarnessRun,
  StartHarnessRunBody,
} from '@borradh-workspace/api-client/types';
import { Play, PlayCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

import { useHarnessScenarios, useStartHarnessRun } from '../../api';
import { StatusChip } from './harness-result';

function buildScenarioRun(scenarioIds: string[]): StartHarnessRunBody {
  return { kind: 'scenarios', scenarioIds };
}

/** Latest result per scenario across the runs we know about. */
function latestResults(runs: HarnessRun[]) {
  const latest = new Map<
    string,
    { item: HarnessItemResult; running: boolean }
  >();
  for (const run of [...runs].sort((a, b) =>
    a.startedAt.localeCompare(b.startedAt)
  )) {
    if (run.kind !== 'scenarios') continue;
    for (const item of run.items) latest.set(item.id, { item, running: false });
  }
  return latest;
}

export function ScenariosPanel({
  disabled,
  runs,
  onRun,
}: {
  disabled: boolean;
  runs: HarnessRun[];
  onRun: (runId: string) => void;
}) {
  const { scenarios, isLoading } = useHarnessScenarios();
  const { startRun, isStarting } = useStartHarnessRun({
    onSuccess: (run) => onRun(run.id),
  });
  const running = runs.some(
    (run) => run.kind === 'scenarios' && run.status === 'running'
  );
  const latest = latestResults(runs);

  const run = (ids: string[]) => {
    const body = buildScenarioRun(ids);
    startRun(body);
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2, 3].map((key) => (
          <Skeleton key={key} className="h-20 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-muted-foreground text-sm">
          Scripted cases with a made-up clinic. Claire’s words are fixed, so
          these test the safety checks and the send path — same result every
          time.
        </p>
        <Button
          onClick={() => run(scenarios.map((scenario) => scenario.id))}
          disabled={disabled || isStarting || running || !scenarios.length}
          className="shrink-0"
        >
          {running ? (
            <Spinner className="size-4" />
          ) : (
            <PlayCircle className="size-4" />
          )}
          Run all
        </Button>
      </div>
      <ul className="space-y-2">
        {scenarios.map((scenario) => {
          const result = latest.get(scenario.id);
          return (
            <li
              key={scenario.id}
              className={cn(
                'group flex items-start gap-3 rounded-xl border p-4 transition-colors hover:bg-muted/40',
                result?.item.status === 'fail' && 'border-rose-500/30',
                result?.item.status === 'error' && 'border-rose-500/30'
              )}
            >
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-medium text-sm">{scenario.title}</p>
                  {result ? <StatusChip status={result.item.status} /> : null}
                </div>
                <p className="text-muted-foreground text-sm">
                  {scenario.summary}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => run([scenario.id])}
                disabled={disabled || isStarting || running}
                aria-label={`Run ${scenario.title}`}
              >
                <Play className="size-3.5" />
                Run
              </Button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
